import { Download, Upload, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ROUTE } from '@/config';
import { useState } from 'react';
import { ExportModal } from './ExportModal/ExportModal';
import dayjs from 'dayjs';
import { useExportFilesAsync, useFetchExportedFiles } from '@/hooksApi';
import { useNavigate } from 'react-router-dom';
import { ProcessNameEnum, usePollingMechanismStore } from '@/stores/pollingMechanismStore';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';

const OrderPageHeader = () => {
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const { startPolling } = usePollingMechanismStore();

  const { refetch: refetchExportedFileList } = useFetchExportedFiles();

  const { mutate: exportFilesAsync } = useExportFilesAsync();

  const handleExport = async (
    selectedDateRange?: string[],
    selectedMonthRange?: string[],
    selectedStatuses?: string[],
    selectedCarriers?: string[],
  ) => {
    const dates = selectedDateRange ?? selectedMonthRange;

    const queryParts = [
      `filter_created_at_from=${dates ? dates[0] : dayjs().subtract(1, 'month').format('YYYY-MM-DD')}`,
      `filter_created_at_to=${dates ? dates[1] : dayjs().format('YYYY-MM-DD')}`,
    ];
    if (selectedStatuses) {
      selectedStatuses.forEach((status) => {
        queryParts.push(`filter_statuses[]=${status}`);
      });
    }
    if (selectedCarriers) {
      selectedCarriers.forEach((carrier) => {
        queryParts.push(`filter_carriers[]=${carrier}`);
      });
    }

    exportFilesAsync(queryParts.join('&'), {
      onSuccess: () => {
        navigate(ROUTE.SETTINGS_FILES);

        startPolling(
          ProcessNameEnum.EXPORT_ORDER,
          {
            fileName: 'fileName',
            toastTextKey: 'export_is_in_queue',
          },
          checkRefetchingExportProcess,
        );
        // addToast({
        //   fileName: 'fileName',
        //   toastTextKey: 'export_is_in_queue',
        //   hideDelay: 8000,
        //   onHideCallback: () => queryClient.invalidateQueries(['exported-file']),
        // });
        // navigate(ROUTE.SETTINGS, { state: { activeTab: SettingSection.File } });
      },
    });
  };

  const checkRefetchingExportProcess = async () => {
    const { data } = await refetchExportedFileList();

    return !!(data?.object?.items && data?.object?.items[0].file);
  };

  const iconBtnClass =
    'w-9 h-9 flex items-center justify-center rounded-xl border border-solid transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/[0.04]';

  return (
    <div className='flex items-center gap-2'>
      <button
        onClick={() => setIsExportModalOpen(true)}
        disabled={!hasPermission(PermissionName.ORDER_AND_SHIPMENT)}
        className={iconBtnClass}
        style={{ borderColor: 'rgb(232, 230, 227)', backgroundColor: '#ffffff' }}
        title={String(t('export'))}
      >
        <Upload className='w-[18px] h-[18px]' style={{ color: 'rgb(115, 123, 140)' }} strokeWidth={1.5} />
      </button>
      <button
        onClick={() => navigate(ROUTE.IMPORT)}
        disabled={!hasPermission(PermissionName.BULK_IMPORT_ORDER)}
        className={iconBtnClass}
        style={{ borderColor: 'rgb(232, 230, 227)', backgroundColor: '#ffffff' }}
        title={String(t('import'))}
      >
        <Download className='w-[18px] h-[18px]' style={{ color: 'rgb(115, 123, 140)' }} strokeWidth={1.5} />
      </button>
      <button
        onClick={() => navigate(ROUTE.ADDNEWORDER)}
        disabled={!hasPermission(PermissionName.CREATE_ORDER)}
        className='flex items-center gap-2 h-9 px-4 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90'
        style={{ backgroundColor: 'rgb(237, 224, 74)', color: 'rgb(57, 57, 60)' }}
      >
        <Plus className='w-4 h-4' strokeWidth={2} />
        {String(t('new_order'))}
      </button>

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
      />
    </div>
  );
};

export default OrderPageHeader;
